import {
  BOOKMARK_TOKEN_PATH,
  type BookmarkTokenRequest,
  bookmarkTokenErrorSchema,
  bookmarkTokenResponseSchema,
} from "@/contracts/bookmark-token";

/**
 * `no_reader`: the session cannot hold a bookmark link; a session opened
 * from a card or a bookmark is the common case. `rate_limited`: ten issues
 * or resets an hour, a returned link not counted. `unavailable`: offline,
 * the broker failed, or an answer the contract does not describe.
 */
export type BookmarkLinkRefusal = "no_reader" | "rate_limited" | "unavailable";

export type BookmarkLinkAnswer =
  | { kind: "link"; url: string; issued: boolean }
  | { kind: "refused"; reason: BookmarkLinkRefusal };

export type Fetch = (input: string, init: RequestInit) => Promise<Response>;

async function refusalOf(response: Response): Promise<BookmarkLinkRefusal> {
  if (response.status === 401) return "no_reader";
  if (response.status === 429) return "rate_limited";
  try {
    const parsed = bookmarkTokenErrorSchema.safeParse(await response.json());
    if (parsed.success && parsed.data.error === "no_reader") return "no_reader";
    if (parsed.success && parsed.data.error === "rate_limited")
      return "rate_limited";
  } catch {
    // An unreadable body says nothing more than the status did.
  }
  return "unavailable";
}

// The session cookie is `/auth`-scoped, so the Worker reads it from the
// request itself; no Bearer token is fetched first.
export async function requestBookmarkLink(
  body: BookmarkTokenRequest,
  fetchImpl: Fetch = (input, init) => fetch(input, init),
  signal?: AbortSignal,
): Promise<BookmarkLinkAnswer> {
  try {
    const response = await fetchImpl(BOOKMARK_TOKEN_PATH, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      ...(signal && { signal }),
    });
    if (!response.ok)
      return { kind: "refused", reason: await refusalOf(response) };
    const parsed = bookmarkTokenResponseSchema.safeParse(await response.json());
    if (!parsed.success) return { kind: "refused", reason: "unavailable" };
    return { kind: "link", url: parsed.data.url, issued: parsed.data.issued };
  } catch {
    return { kind: "refused", reason: "unavailable" };
  }
}
